import { useState, useEffect } from "react";
import { Box, Flex, Text } from "@chakra-ui/react";
import { CustomerStarterKit } from "../types";
import { Banner } from "../../banners/types";
import moroccanoilImage from "../../../assets/starter-kits/moroccanoil.png";
import oligoImage from "../../../assets/starter-kits/oligo.png";
import mydentityImage from "../../../assets/starter-kits/mydentity.png";

const API_BASE_URL = process.env.VITE_ADMIN_BASE_URL;

type BrandName = "Moroccanoil" | "Oligo" | "Mydentity";

const brandImages: Record<BrandName, string> = {
  Moroccanoil: moroccanoilImage,
  Oligo: oligoImage,
  Mydentity: mydentityImage,
};

const brandStyles: Record<BrandName, { bg: string; scale: number }> = {
  Moroccanoil: { bg: "#16BCBD", scale: 1.02 },
  Oligo: { bg: "#ec1c59", scale: 1.02 },
  Mydentity: { bg: "#522d6d", scale: 1.02 },
};

type CarouselItem =
  | { type: "starterKit"; data: CustomerStarterKit }
  | { type: "banner"; data: Banner };

interface StarterKitCarouselProps {
  customerStarterKits?: CustomerStarterKit[];
  banners?: Banner[];
}

export const StarterKitCarousel = ({
  customerStarterKits = [],
  banners = [],
}: StarterKitCarouselProps) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);

  const carouselItems: CarouselItem[] = [
    ...banners.map(banner => ({ type: "banner" as const, data: banner })),
    ...customerStarterKits.map(kit => ({
      type: "starterKit" as const,
      data: kit,
    })),
  ];

  useEffect(() => {
    if (carouselItems.length <= 1) return;

    const interval = setInterval(() => {
      handleNext();
    }, 6000);

    return () => clearInterval(interval);
  }, [currentIndex, carouselItems.length]);

  const fadeToIndex = (index: number) => {
    if (isAnimating || index === currentIndex) return;

    setIsAnimating(true);

    setTimeout(() => {
      setCurrentIndex(index);
      setIsAnimating(false);
    }, 500);
  };

  const handleNext = () => {
    if (carouselItems.length <= 1) return;
    const newIndex =
      currentIndex === carouselItems.length - 1 ? 0 : currentIndex + 1;
    fadeToIndex(newIndex);
  };

  if (carouselItems.length === 0) return null;

  const currentItem = carouselItems[currentIndex];

  const renderBanner = (banner: Banner) => {
    const bgColor = banner.linkBgColor || "#FFFFFF";
    const textColor = "#FFFFFF";

    const linkProps = banner.linkUrl
      ? {
          as: "a" as const,
          href: banner.linkUrl,
          target: "_blank" as const,
          cursor: "pointer" as const,
        }
      : {
          as: "div" as const,
          cursor: "default" as const,
        };

    return (
      <Box
        position="relative"
        w="full"
        aspectRatio={{ base: 2 / 1, md: 3 / 1 }}
        bg="gray.100"
      >
        <img
          src={banner.imageUrl}
          alt={banner.title}
          style={{
            width: "100%",
            height: "100%",
            objectFit: "cover",
            objectPosition: "center",
            position: "absolute",
            top: 0,
            left: 0,
          }}
        />
        <Box
          position="absolute"
          top="0"
          left="0"
          right="0"
          bottom="0"
          bg="blackAlpha.700"
        />
        <Flex
          {...linkProps}
          position="absolute"
          direction="column"
          width="full"
          height="full"
          justifyContent="center"
          px={{ base: 4, sm: 6, md: 8, lg: 12 }}
          color="white"
          transition="all 0.2s"
          sx={{
            textDecoration: "none",
          }}
        >
          <Text
            fontSize={{ base: "md", sm: "lg", md: "xl", lg: "2xl" }}
            fontWeight="bold"
            lineHeight={1.2}
            mb={{ base: 1, md: 2 }}
            dangerouslySetInnerHTML={{ __html: banner.title }}
            noOfLines={2}
          />
          <Text
            fontSize={{ base: "sm", sm: "sm", md: "md" }}
            mb={{ base: 2, md: 4 }}
            dangerouslySetInnerHTML={{ __html: banner.description }}
            noOfLines={{ base: 4, sm: 4, md: 4, lg: 5 }}
          />
          {banner.linkUrl && banner.linkLabel && (
            <Box
              display={{ base: "none", md: "block" }}
              width="fit-content"
              bgColor={bgColor}
              color={textColor}
              px={{ base: 3, sm: 4 }}
              py={{ base: "6px", sm: 2 }}
              borderRadius="md"
              fontWeight="400"
              fontSize={{ base: "sm", sm: "base" }}
              mt={{ base: 1, md: 2 }}
              pointerEvents="none"
              sx={{
                WebkitFontSmoothing: "subpixel-antialiased",
                backfaceVisibility: "hidden",
              }}
              dangerouslySetInnerHTML={{
                __html: banner.linkLabel || "Meer informatie",
              }}
            />
          )}
        </Flex>
      </Box>
    );
  };

  const renderStarterKit = (starterKit: CustomerStarterKit) => {
    const currentBrand = starterKit.starterkit.brand.name as BrandName;
    const currentCustomerStarterkitId = starterKit.id;

    const isValidBrand = Object.keys(brandImages).includes(currentBrand);

    const defaultButtonStyle = { bg: "#333333", scale: 1.02 };

    const buttonBrandStyles = isValidBrand
      ? brandStyles[currentBrand]
      : defaultButtonStyle;

    const bannerTitle =
      starterKit.starterkit.bannerTitle ||
      `Ontdek het ${currentBrand} startpakket`;
    const bannerDescription =
      starterKit.starterkit.bannerDescription ||
      "Stel je eigen startpakket samen met korting!";
    const buttonText =
      starterKit.starterkit.bannerLinkLabel || "Stel je pakket samen";

    const imageUrl =
      starterKit.starterkit.imageUrl ||
      (isValidBrand ? brandImages[currentBrand] : undefined);

    const linkUrl = `${API_BASE_URL}/customer-starter-kit/${currentCustomerStarterkitId}`;

    return (
      <Box
        position="relative"
        w="full"
        aspectRatio={{ base: 2 / 1, md: 3 / 1 }}
        bg="gray.100"
      >
        {imageUrl && (
          <img
            src={imageUrl}
            alt={bannerTitle}
            style={{
              width: "100%",
              height: "100%",
              objectFit: "cover",
              objectPosition: "center",
              position: "absolute",
              top: 0,
              left: 0,
            }}
          />
        )}

        <Box
          position="absolute"
          top="0"
          left="0"
          right="0"
          bottom="0"
          bg="blackAlpha.700"
        />

        <Flex
          as="a"
          href={linkUrl}
          target="_blank"
          position="absolute"
          direction="column"
          width="full"
          height="full"
          justifyContent="center"
          px={{ base: 4, sm: 6, md: 8, lg: 12 }}
          color="white"
          cursor="pointer"
          transition="all 0.2s"
          sx={{
            textDecoration: "none",
          }}
        >
          <Text
            fontSize={{ base: "md", sm: "lg", md: "xl", lg: "2xl" }}
            fontWeight="bold"
            lineHeight={1.2}
            mb={{ base: 1, md: 2 }}
            dangerouslySetInnerHTML={{ __html: bannerTitle }}
            noOfLines={2}
          />

          <Text
            fontSize={{ base: "sm", sm: "sm", md: "md" }}
            mb={{ base: 2, md: 4 }}
            dangerouslySetInnerHTML={{ __html: bannerDescription }}
            noOfLines={{ base: 4, sm: 4, md: 4, lg: 5 }}
          />

          <Box
            display={{ base: "none", md: "block" }}
            width="fit-content"
            bgColor={buttonBrandStyles.bg}
            color="white"
            px={{ base: 3, sm: 4 }}
            py={{ base: "6px", sm: 2 }}
            borderRadius="md"
            fontWeight="400"
            fontSize={{ base: "sm", sm: "base" }}
            mt={{ base: 1, md: 2 }}
            pointerEvents="none"
            sx={{
              WebkitFontSmoothing: "subpixel-antialiased",
              backfaceVisibility: "hidden",
            }}
            dangerouslySetInnerHTML={{ __html: buttonText }}
          />
        </Flex>
      </Box>
    );
  };

  return (
    <Box
      position="relative"
      w="full"
      overflow="hidden"
      borderRadius="md"
      mb={4}
      zIndex={1}
    >
      {}
      <Box opacity={isAnimating ? 0 : 1} transition="opacity 0.5s ease-in-out">
        {currentItem.type === "banner"
          ? renderBanner(currentItem.data)
          : renderStarterKit(currentItem.data)}
      </Box>

      {}
      {carouselItems.length > 1 && (
        <Flex
          position="absolute"
          bottom={4}
          left="50%"
          transform="translateX(-50%)"
          gap={2}
          justify="center"
        >
          {carouselItems.map((_, index) => (
            <Box
              key={index}
              h="2"
              w="2"
              borderRadius="full"
              bg={index === currentIndex ? "white" : "whiteAlpha.600"}
              cursor="pointer"
              onClick={() => fadeToIndex(index)}
            />
          ))}
        </Flex>
      )}
    </Box>
  );
};
